import React, { useState, useEffect, useRef } from "react";
import "./ColumnFilter.css";
import { Checkbox } from "./Components/Checkbox/Checkbox";
import { primaryColumnFields, additionalColumnFields } from "./Column.fields";

/* TYPES */
interface ColumnState {
  selected: boolean;
  isFlagged: boolean;
  Status: boolean;
  ID: boolean;
  User: boolean;
  Date: boolean;
  Supplier: boolean;
  Category: boolean;
  Total: boolean;
  Tax: boolean;
  "Submitted By": boolean;
  "Due Date": boolean;
  "Document Ref": boolean;
  baseTotal: boolean;
  Description: boolean;
  Paid: boolean;
  Payment: boolean;
  Type: boolean;
  itemId: boolean;
  "Submission Method": boolean;
  noteMessage: boolean;
  "Upload Date": boolean;
  exportDate: boolean;
  Match: boolean;
  Taxrate: boolean;
}

interface ColumnSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onApply?: (selectedColumns: string[]) => void;
}

/* DEFAULT STATE */
const defaultColumns: ColumnState = {
  selected: true,
  isFlagged: true,
  ID: true,
  Status: true,
  User: false,
  Date: false,
  Supplier: false,
  Category: false,
  Total: false,
  Tax: false,
  "Submitted By": true,
  "Due Date": false,
  "Document Ref": false,
  baseTotal: false,
  Description: false,
  Paid: false,
  Payment: false,
  Type: false,
  itemId: false,
  "Submission Method": false,
  noteMessage: false,
  "Upload Date": false,
  exportDate: false,
  Match: false,
  Taxrate: false,
};

const ColumnSettings: React.FC<ColumnSettingsProps> = ({
  isOpen,
  onClose,
  onApply,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const DEFAULT_COLUMNS = [
    "selected",
    "isFlagged",
    "ID",
    "Status",
    "Submitted By",
    "Date",
    "Supplier",
    "Total",
    "Tax",
    "Document Ref",
    "Description",
    "Category",
  ];

  const [columns, setColumns] = useState<ColumnState>(() => {
    const saved = localStorage.getItem("selectedColumns");

    const base = { ...defaultColumns };

    if (saved) {
      const parsed: string[] = JSON.parse(saved);
      parsed.forEach((col) => {
        if (col in base) {
          base[col as keyof ColumnState] = true;
        }
      });
    } else {
      // first time → apply DEFAULT_COLUMNS
      DEFAULT_COLUMNS.forEach((col) => {
        if (col in base) {
          base[col as keyof ColumnState] = true;
        }
      });

      localStorage.setItem("selectedColumns", JSON.stringify(DEFAULT_COLUMNS));
    }

    return base;
  });

  /*  LOAD FROM LOCALSTORAGE OR SAVE DEFAULT */
  // useEffect(() => {
  //   const saved = localStorage.getItem("selectedColumns");

  //   if (saved) {
  //     const parsed: string[] = JSON.parse(saved);
  //     const restored = { ...defaultColumns };

  //     parsed.forEach((col) => {
  //       if (col in restored) {
  //         restored[col as keyof ColumnState] = true;
  //       }
  //     });

  //     setColumns(restored);
  //   } else {
  //     // First time → save default columns
  //     const defaultSelected = Object.keys(defaultColumns).filter(
  //       (key) => defaultColumns[key as keyof ColumnState],
  //     );
  //     localStorage.setItem("selectedColumns", JSON.stringify(defaultSelected));
  //     setColumns(defaultColumns);
  //   }
  // }, []);

  /*  TOGGLE + update localStorage immediately */
  const handleToggle = (key: keyof ColumnState) => {
    setColumns((prev) => {
      const newState = { ...prev, [key]: !prev[key] };

      // Save only checked columns in localStorage
      const selectedColumns = Object.keys(newState).filter(
        (k) => newState[k as keyof ColumnState],
      );
      // if (selectedColumns.length > 0) {
      //   localStorage.setItem(
      //     "selectedColumns",
      //     JSON.stringify(selectedColumns),
      //   );
      // } else {
      //   localStorage.removeItem("selectedColumns");
      // }

      return newState;
    });
  };

  /*  RESET → uncheck all + remove localStorage */
  const RESET_DEFAULT_COLUMNS: (keyof ColumnState)[] = [
    "selected",
    "isFlagged",
    "ID",
    "Status",
    "Date",
    "Supplier",
    "Total",
    "Tax",
    "Document Ref", // Document Ref
    "Description",
    "Category",
    "Submitted By",
  ];
  const handleReset = () => {
    const resetState = Object.keys(defaultColumns).reduce((acc, key) => {
      acc[key as keyof ColumnState] = RESET_DEFAULT_COLUMNS.includes(
        key as keyof ColumnState,
      );
      return acc;
    }, {} as ColumnState);

    setColumns(resetState);

    // save reset default columns in order
    localStorage.setItem(
      "selectedColumns",
      JSON.stringify(RESET_DEFAULT_COLUMNS),
    );
    onApply?.(RESET_DEFAULT_COLUMNS);
  };

  /*  APPLY  */
  const handleApply = () => {
    const selectedColumns = Object.keys(columns).filter(
      (key) => columns[key as keyof ColumnState],
    );

    // alert(`Selected columns: ${selectedColumns.join(", ")}`);
    localStorage.setItem("selectedColumns", JSON.stringify(selectedColumns));
    onApply?.(selectedColumns);
    onClose();
  };

  /*  CLOSE ON OUTSIDE CLICK  */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="overlay">
      <div className="popup-container" ref={popupRef}>
        {/* PRIMARY */}
        <div className="section">
          <p
            className="section-title"
            style={{
              backgroundColor: "var(--color-bg-surface)",
              padding: "20px",
              paddingLeft: 0,
              position: "sticky",
              top: 0,
              marginTop: 0,
            }}
          >
            PRIMARY COLUMNS
          </p>
          <div className="grid-layout">
            {primaryColumnFields.map((field) => (
              <Checkbox
                key={field.id}
                hidden={field.hidden}
                disabled={field.disabled}
                label={field.label}
                checked={columns[field.id as keyof ColumnState]}
                onChange={() => handleToggle(field.id as keyof ColumnState)}
              />
            ))}
          </div>
        </div>

        {/* ADDITIONAL */}
        <div className="section">
          <p className="section-title">ADDITIONAL COLUMNS</p>
          <div className="grid-layout">
            {additionalColumnFields.map((field) => (
              <Checkbox
                key={field.id}
                hidden={field.hidden}
                disabled={field.disabled}
                label={field.label}
                checked={!!columns[field.id as keyof ColumnState]}
                onChange={() => handleToggle(field.id as keyof ColumnState)}
              />
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div className="footer-actions">
          <span className="reset-btn" onClick={handleReset}>
            Reset
          </span>
          <button className="apply-btn" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnSettings;
